import { put } from "@selector/shared";
import type { components } from "@selector/shared/openapi";
import { useContext, useRef, useState } from "react";
import { DialogComponent, Modal, PanelOrientation, PanelPosition, ResizableTabPanels, TriggerButton } from "widgets";
import { useOverlay } from "widgets/hooks";

import { ColumnTemplatesProvider } from "./column-templates/ColumnTemplatesContext.tsx";
import { ColumnTemplatesPanel } from "./column-templates/ColumnTemplatesPanel.tsx";
import { ColumnTemplatesContext } from "./column-templates/context.ts";
import { collectColumnsFromGrid } from "./column-templates/utils";
import { ColumnFilter } from "./ColumnFilter.tsx";
import { ColumnSelectionGrid } from "./ColumnSelectionGrid.tsx";

export type TreeColumnType = components["schemas"]["ColumnSparseResponse"];

interface ColumnSelectionPopoverProps {
  baseRequest: Partial<components["schemas"]["TableRequest"]>;

  tableSpecForTemplates: Partial<components["schemas"]["TableRequest"]>;
  initialValueColumns?: TreeColumnType[];
  isViewMode?: boolean;
  onApply: (columns: TreeColumnType[]) => void;
}

export const ColumnSelectionPopover = ({
  baseRequest,
  tableSpecForTemplates,
  initialValueColumns,
  isViewMode = false,
  onApply,
}: ColumnSelectionPopoverProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay();

  return (
    <>
      <TriggerButton {...triggerProps} icon="table_edit" label="Choose Columns" ref={triggerRef} />
      <Modal state={state} {...overlayProps}>
        <ColumnTemplatesProvider
          tableSpecForTemplates={tableSpecForTemplates}
          baseRequest={baseRequest}
          isOpen={state.isOpen}
          initialValueColumns={initialValueColumns}
        >
          <ColumnSelectionModal closeFn={state.close} onApply={onApply} isViewMode={isViewMode} />
        </ColumnTemplatesProvider>
      </Modal>
    </>
  );
};

const ColumnSelectionModal = ({
  closeFn,
  onApply,
  isViewMode,
}: {
  closeFn: () => void;
  onApply: (columns: TreeColumnType[]) => void;
  isViewMode: boolean;
}) => {
  const { gridApi, tableSpecForTemplates } = useContext(ColumnTemplatesContext);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const handleApply = () => {
    setErrorMessage(undefined);

    if (!gridApi.current) {
      setErrorMessage("Columns are still loading. Please try again.");
      return;
    }

    const { resultType, queryType } = tableSpecForTemplates;

    if (!resultType || !queryType) {
      setErrorMessage("The table information required to save this template is unavailable.");
      return;
    }

    setIsSaving(true);
    const columns = collectColumnsFromGrid(gridApi.current);

    if (isViewMode) {
      onApply(columns);
      closeFn();
      setIsSaving(false);
      return;
    }

    put("/template", undefined, undefined, {
      columns,
      resultType,
      queryType,
    })
      .then(() => {
        setErrorMessage(undefined);
        onApply(columns);
        closeFn();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <DialogComponent
      title="Choose Columns"
      icon="table_edit"
      width={690}
      closeFn={closeFn}
      contentStyle={{ padding: 0, display: "flex", flexDirection: "column", minHeight: 420 }}
      asPanelHeader
      // style={{ overflow: "hidden" }}
      mainButton={{
        label: "Apply",
        onPress: handleApply,
        isLoading: isSaving,
      }}
      infoComponent={<ColumnFilter key={"filter"} />}
      errorCallout={errorMessage ? { message: errorMessage } : undefined}
    >
      <ResizableTabPanels
        orientation={PanelOrientation.Horizontal}
        fixedPanel={PanelPosition.Trailing}
        defaultSize={280}
        style={{ width: "100%", maxHeight: "100%", flex: 1, overflow: "hidden" }}
        minSize={280}
        noResize
      >
        <ResizableTabPanels.FlexPanel>
          <ColumnSelectionGrid />
        </ResizableTabPanels.FlexPanel>
        <ResizableTabPanels.FixedPanel>
          <ColumnTemplatesPanel />
        </ResizableTabPanels.FixedPanel>
      </ResizableTabPanels>
    </DialogComponent>
  );
};
